import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, Warning } from "@phosphor-icons/react";
import {
  fetchHealthSummary,
  type HealthSummary,
  type HealthService,
} from "../lib/healthClient";

const POLL_INTERVAL_MS = 60_000;

const INCIDENTS = [
  {
    date: "March 22, 2026",
    title: "Scheduled maintenance — database migration",
    status: "resolved",
    description:
      "A planned 15-minute maintenance window for database schema upgrades. All services were restored on schedule with no data loss.",
  },
  {
    date: "March 10, 2026",
    title: "Elevated API latency in EU region",
    status: "resolved",
    description:
      "API response times were elevated for approximately 25 minutes due to a network routing issue at our cloud provider. Automatically mitigated by failover.",
  },
  {
    date: "February 28, 2026",
    title: "Notification delivery delays",
    status: "resolved",
    description:
      "Email and push notifications experienced delays of up to 10 minutes due to a queue backlog. Resolved by scaling notification workers.",
  },
];

const STATUS_LABEL: Record<HealthService["status"], string> = {
  ok: "Operational",
  degraded: "Degraded",
  down: "Outage",
};

const STATUS_DOT: Record<HealthService["status"], string> = {
  ok: "bg-emerald-500",
  degraded: "bg-amber-500",
  down: "bg-rose-500",
};

const STATUS_TEXT: Record<HealthService["status"], string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  degraded: "text-amber-600 dark:text-amber-400",
  down: "text-rose-600 dark:text-rose-400",
};

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return new Date(iso).toLocaleTimeString();
}

export function StatusPage() {
  const [health, setHealth] = useState<HealthSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    const tick = async () => {
      try {
        const summary = await fetchHealthSummary(ctrl.signal);
        if (cancelled) return;
        setHealth(summary);
        setError(null);
      } catch (e) {
        if (cancelled || (e instanceof Error && e.name === "AbortError")) return;
        setError(e instanceof Error ? e.message : "Health check failed");
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      ctrl.abort();
      window.clearInterval(id);
    };
  }, []);

  const overall: HealthService["status"] | "unknown" = error
    ? "unknown"
    : (health?.status ?? "unknown");

  return (
    <main className="pt-28 pb-20">
      <div className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          System Status
        </h1>

        {/* Overall status banner */}
        <OverallBanner overall={overall} loading={initialLoading} fetchedAt={health?.fetchedAt} />

        {/* Service list */}
        <div className="mt-10">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Services</h2>
          <div className="mt-4 rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 divide-y divide-slate-200/60 dark:divide-slate-800/60">
            {initialLoading && !health ? (
              <ServiceRowSkeleton />
            ) : error && !health ? (
              <div className="px-6 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                Unable to reach the status endpoint. Showing no live data.
              </div>
            ) : (
              health!.services.map((service) => (
                <div
                  key={service.name}
                  className="flex items-center justify-between px-6 py-3.5"
                >
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {service.name}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-medium ${STATUS_TEXT[service.status]}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${STATUS_DOT[service.status]}`} />
                    {STATUS_LABEL[service.status]}
                    {typeof service.latencyMs === "number" && (
                      <span className="text-slate-400 dark:text-slate-500 font-normal">
                        · {service.latencyMs}ms
                      </span>
                    )}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent incidents (still hardcoded — no incident table on backend) */}
        <div className="mt-10">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            Recent Incidents
          </h2>
          <div className="mt-4 space-y-6">
            {INCIDENTS.map((incident) => (
              <div key={incident.title}>
                <div className="flex items-center gap-3">
                  <Clock
                    size={16}
                    className="text-slate-400 dark:text-slate-500 shrink-0"
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {incident.date}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-950/30 px-2 py-0.5 text-xs font-semibold uppercase text-emerald-700 dark:text-emerald-400">
                    {incident.status}
                  </span>
                </div>
                <h3 className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">
                  {incident.title}
                </h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {incident.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

function OverallBanner({
  overall,
  loading,
  fetchedAt,
}: {
  overall: HealthService["status"] | "unknown";
  loading: boolean;
  fetchedAt: string | undefined;
}) {
  const tone =
    overall === "ok"
      ? {
          bg: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200/60 dark:border-emerald-800/40",
          text: "text-emerald-800 dark:text-emerald-300",
          icon: <CheckCircle size={32} weight="duotone" className="mx-auto text-emerald-600 dark:text-emerald-400" />,
          label: "All Systems Operational",
        }
      : overall === "degraded"
        ? {
            bg: "bg-amber-50 dark:bg-amber-950/30 border-amber-200/60 dark:border-amber-800/40",
            text: "text-amber-800 dark:text-amber-300",
            icon: <Warning size={32} weight="duotone" className="mx-auto text-amber-600 dark:text-amber-400" />,
            label: "Some Systems Experiencing Issues",
          }
        : overall === "down"
          ? {
              bg: "bg-rose-50 dark:bg-rose-950/30 border-rose-200/60 dark:border-rose-800/40",
              text: "text-rose-800 dark:text-rose-300",
              icon: <Warning size={32} weight="duotone" className="mx-auto text-rose-600 dark:text-rose-400" />,
              label: "Service Outage",
            }
          : {
              bg: "bg-slate-50 dark:bg-slate-900/30 border-slate-200/60 dark:border-slate-800/40",
              text: "text-slate-700 dark:text-slate-300",
              icon: (
                <Clock
                  size={32}
                  weight="duotone"
                  className="mx-auto text-slate-500 dark:text-slate-400"
                />
              ),
              label: loading ? "Checking system status…" : "Status unavailable",
            };

  return (
    <div className={`mt-6 rounded-2xl p-6 text-center border ${tone.bg}`}>
      {tone.icon}
      <p className={`mt-2 text-lg font-semibold ${tone.text}`}>{tone.label}</p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {fetchedAt ? `Last checked: ${formatRelativeTime(fetchedAt)}` : "Last checked: —"}
      </p>
    </div>
  );
}

function ServiceRowSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between px-6 py-3.5">
          <div className="h-3 w-32 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
          <div className="h-3 w-20 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
        </div>
      ))}
    </>
  );
}
